// src/pages/ProxyPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Modal,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  Grid,
  Chip,
  Pagination, Checkbox, FormControlLabel, InputAdornment
} from '@mui/material';
import { Edit, Delete, Link as LinkIcon, Visibility, VisibilityOff, Info } from '@mui/icons-material';
import api from '../api/api';
import { Link } from "react-router-dom";

interface Account {
  id: string;
  name: string;
  phone_number: string;
  status: string;
  meta_info?: string | null;
}

interface Proxy {
  id: string;
  host: string;
  port: number;
  proxy_type: string;
  last_used: string | null;
  is_active: boolean;
  linked_accounts: Account[];
}

interface ProxyListResponse {
  proxys: Proxy[];
  total_count: number;
}



const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '12px',
  maxHeight: '90vh',
  overflow: 'auto'
};

const ProxyPage: React.FC = () => {
  const [proxies, setProxies] = useState<Proxy[]>([]);
  const [totalProxies, setTotalProxies] = useState(0);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState(false);

  const [editProxy, setEditProxy] = useState<Proxy | null>(null);
  const [linkProxyId, setLinkProxyId] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [proxy_type, setProxyType] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState('');
  const [is_active, setIsActive] = useState(true);

  // Пагинация
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [proxyRes, accRes] = await Promise.all([
        api.get<ProxyListResponse>('/proxy', {
          params: { limit: pageSize, offset: (page - 1) * pageSize },
        }),
        api.get<{ accounts: Account[] }>('/accounts'),
      ]);

      setProxies(proxyRes.data.proxys || []);
      setTotalProxies(proxyRes.data.total_count || 0);
      setAccounts(accRes.data.accounts || []);
      setMessage(null);
    } catch (error: any) {
      console.error('Ошибка загрузки:', error);
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const createProxy = async () => {
    if (!host || !port || !proxy_type) {
      setMessage({ text: 'Заполните хост, порт и тип прокси', type: 'error' });
      return;
    }
    if (proxy_type === 'MTPROTO' && !secret) {
      setMessage({ text: 'Для MTPROTO нужен secret', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/proxy/create', {
        proxy_type,
        host,
        port: Number(port),
        username: ['HTTP', 'SOCKS5'].includes(proxy_type) ? username || null : null,
        password: ['HTTP', 'SOCKS5'].includes(proxy_type) ? password || null : null,
        secret: ['MTPROTO', 'SOCKS4'].includes(proxy_type) ? secret || null : null,
      });
      setMessage({ text: 'Прокси создан!', type: 'success' });
      setOpenCreateModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateProxy = async () => {
    if (!editProxy || !host || !port || !proxy_type) return;
    if (proxy_type === 'MTPROTO' && !secret) {
      setMessage({ text: 'Для MTPROTO нужен secret', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.patch(`/proxy/${editProxy.id}`, {
        proxy_type: proxy_type || undefined,
        host: host || undefined,
        port: port ? Number(port) : undefined,
        username: ['HTTP', 'SOCKS5'].includes(proxy_type) ? username || null : null,
        password: ['HTTP', 'SOCKS5'].includes(proxy_type) ? password || null : null,
        secret: ['MTPROTO', 'SOCKS4'].includes(proxy_type) ? secret || null : null,
        is_active,
      });
      setProxies(p =>
          p.map(x =>
              x.id === editProxy.id
                  ? {...x, ...res.data}
                  : x
          )
      );
      setMessage({ text: 'Прокси обновлён!', type: 'success' });
      setOpenUpdateModal(false);
      resetForm();
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProxy = async (id: string) => {
    if (!window.confirm('Удалить прокси навсегда?')) return;
    setIsLoading(true);
    try {
      await api.delete(`/proxy/${id}`);
      setMessage({ text: 'Прокси удалён!', type: 'success' });
      fetchData();
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const linkToAccount = async () => {
    if (!linkProxyId || !selectedAccountId) return;
    setIsLoading(true);
    try {
      await api.post('/proxy/link_to_acc', {
        account_id: selectedAccountId,
        proxy_id: linkProxyId,
      });
      setMessage({ text: 'Прокси привязан!', type: 'success' });
      fetchData();
      setOpenLinkModal(false);
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const unlinkFromAccount = async () => {
    if (!linkProxyId || !selectedAccountId) return;
    setIsLoading(true);
    try {
      await api.post('/proxy/unlink_from_acc', {
        account_id: selectedAccountId,
        proxy_id: linkProxyId,
      });
      setMessage({ text: 'Прокси отвязан!', type: 'success' });
      fetchData();
      setOpenLinkModal(false);
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const openUpdate = (proxy: Proxy) => {
    setEditProxy(proxy);
    setProxyType(proxy.proxy_type);
    setHost(proxy.host);
    setPort(proxy.port.toString());
    setUsername('');
    setPassword('');
    setSecret('');
    setIsActive(proxy.is_active);
    setOpenUpdateModal(true);
  };

  const openLink = (id: string) => {
    setLinkProxyId(id);
    setSelectedAccountId(null);
    setOpenLinkModal(true);
  };

  const resetForm = () => {
    setProxyType('');
    setHost('');
    setPort('');
    setUsername('');
    setPassword('');
    setSecret('');
    setIsActive(true);
    setEditProxy(null);
  };

  const getProxyTypeInfo = () => {
    const types = {
      // HTTP: { desc: 'Для веб-запросов. Поддерживает логин/пароль.', auth: true, secret: false },
      // SOCKS4: { desc: 'Только TCP. Без аутентификации. Используйте secret.', auth: false, secret: true },
      SOCKS5: { desc: 'TCP + UDP. Полная поддержка. Лучший выбор для Telegram.', auth: true, secret: true },
      MTPROTO: { desc: 'Нативный протокол Telegram. Только secret.', auth: false, secret: true }
    };
    return types[proxy_type as keyof typeof types] || { desc: 'Выберите тип', auth: false, secret: false };
  };

  const totalPages = Math.ceil(totalProxies / pageSize);

  if (isLoading && proxies.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === ПОДРОБНОЕ ОПИСАНИЕ — КАК В НЕЙРОКОММЕНТИНГЕ === */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работают прокси в системе?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Прокси — это ваш «щит» от блокировок. Каждый аккаунт может работать через отдельный IP-адрес. Это критично для массового парсинга, нейрокомментинга и рассылок. Без прокси Telegram быстро блокирует аккаунты за подозрительную активность.
        </Typography>

        {/* === 3 КОЛОНКИ: ЗАЧЕМ / КАК ПРИВЯЗАТЬ / ГДЕ ВЗЯТЬ === */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Зачем нужны прокси?</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • Избежать банов Telegram<br />
              • Распределять нагрузку по разным IP<br />
              • Обход региональных ограничений<br />
              • Увеличить лимиты на действия
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Как привязать прокси к аккаунту?</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              <strong>Вариант 1: Фиксированная привязка</strong><br />
              → Нажмите <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> → выберите аккаунт<br />
              → Аккаунт будет использовать <strong>ТОЛЬКО этот прокси</strong>
            </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mt: 1 }}>
              <strong>Вариант 2: Автоматическая ротация</strong><br />
              → Не привязывайте прокси<br />
              → Аккаунт будет <strong>сам ротировать все активные прокси, выбирая из доступных прокси вашего профиля</strong><br />
              → Подходит для масштабирования и безопасности
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>
              Где взять прокси?
            </Typography>

            <Typography sx={{color: '#4B5563', fontSize: '0.9rem', mb: 1}}>
              • <strong>Купить надежные прокси:</strong>{' '}
              <Typography
                  component="a"
                  href="https://proxys.io/?refid=329495"
                  target="_blank"
                  rel="noopener"
                  sx={{textDecoration: 'underline', cursor: 'pointer', color: '#2563EB'}}
              >
                Proxys.io
              </Typography>
              <br/>

              • <strong>Формат:</strong>{' '}
              <code>ip:port:login:pass</code> или <code>ip:port:secret</code>
              <br/>

              • <strong>Рекомендация:</strong> SOCKS5 или MTPROTO
            </Typography>
          </Box>
        </Box>

        {/* === ПОДРОБНО О ТИПАХ ПРОКСИ === */}
        <Box sx={{ bgcolor: '#F3F4F6', p: 3, borderRadius: 2, mb: 3 }}>
          <Typography sx={{ color: '#1E3A8A', fontWeight: 600, mb: 2 }}>
            Поддерживаемые типы прокси
          </Typography>
          <Grid container spacing={2}>
          {/*  <Grid size={{ xs: 12, sm: 6 }}>*/}
          {/*    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E7EB' }}>*/}
          {/*      <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>HTTP</Typography>*/}
          {/*      <Typography sx={{ fontSize: '0.85rem', color: '#4B5563' }}>*/}
          {/*        • Для веб-запросов (браузер, API)<br />*/}
          {/*        • Поддерживает логин и пароль<br />*/}
          {/*        • <strong>Не подходит для Telegram</strong>*/}
          {/*      </Typography>*/}
          {/*    </Box>*/}
          {/*  </Grid>*/}

            {/*<Grid size={{ xs: 12, sm: 6 }}>*/}
            {/*  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E7EB' }}>*/}
            {/*    <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>SOCKS4</Typography>*/}
            {/*    <Typography sx={{ fontSize: '0.85rem', color: '#4B5563' }}>*/}
            {/*      • Только TCP-соединения<br />*/}
            {/*      • Без аутентификации<br />*/}
            {/*      • Используйте <code>secret</code> для настройки<br />*/}
            {/*      • Устаревший, редко используется*/}
            {/*    </Typography>*/}
            {/*  </Box>*/}
            {/*</Grid>*/}

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E7EB', borderLeft: '4px solid #10B981' }}>
                <Typography sx={{ fontWeight: 600, color: '#059669', mb: 1 }}>SOCKS5 (Рекомендуется)</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#4B5563' }}>
                  • TCP + UDP<br />
                  • Полная поддержка аутентификации<br />
                  • Логин/пароль + secret<br />
                  • <strong>Лучший выбор для Telegram</strong><br />
                  • Высокая скорость, стабильность
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E7EB', borderLeft: '4px solid #059669' }}>
                <Typography sx={{ fontWeight: 600, color: '#059669', mb: 1 }}>MTPROTO (Нативный Telegram)</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#4B5563' }}>
                  • Собственный протокол Telegram<br />
                  • Требует только <code>secret</code><br />
                  • Максимальная совместимость<br />
                  • <strong>Идеален для рассылок и парсинга</strong><br />
                  • Минимальная задержка
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* === РЕКОМЕНДАЦИИ — ПОДРОБНО === */}
        <Box sx={{ bgcolor: '#EBF8FF', p: 3, borderRadius: 2, borderLeft: '4px solid #1E40AF', mb: 3 }}>
          <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Ключевые рекомендации:</Typography>
          <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', lineHeight: 1.6 }}>
            • <strong>1 прокси на 3-5 аккаунтов</strong> — оптимальное соотношение<br />
            • <strong>Привязывайте прокси той же страны, где зарегистрирован аккаунт</strong> (Россия → RU IP)<br />
            • Используйте <strong>резидентские прокси</strong> с динамическим IP - на такие можно подвесить куда больше аккаунтов без риска бана<br />
            • <strong>Не используйте бесплатные прокси</strong> — они медленные и быстро попадают в бан
          </Typography>
        </Box>

        {/* === СОВЕТ ПО БЕЗОПАСНОСТИ === */}
        <Box sx={{ bgcolor: '#FEF3C7', p: 3, borderRadius: 2, borderLeft: '4px solid #F59E0B', mb: 3 }}>
          <Typography sx={{ color: '#D97706', fontWeight: 500, mb: 1 }}>Важно:</Typography>
          <Typography sx={{ color: '#92400E', fontSize: '0.9rem' }}>
            <strong>Старайтесь использовать фиксированную привязку (1 прокси = 3-5 аккаунтов)</strong> для максимальной безопасности.<br />
            <strong>Автоматическая ротация внутри нашего сервиса</strong> — удобна, но риск банов ваших аккаунтов возрастет.
          </Typography>
        </Box>

        <Typography sx={{ color: '#4B5563', fontStyle: 'italic', fontSize: '0.95rem' }}>
          Наличие ваших зарегистрированных в системе прокси является <strong>обязательным</strong> условием для работы с Typhon.
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
            Мои прокси
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenCreateModal(true)}
            disabled={isLoading}
            sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
          >
            Создать прокси
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {proxies.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>IP:Порт</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Тип</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Привязанные аккаунты</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Статус</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Использован</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proxies.map(p => (
                    <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.host}:{p.port}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.proxy_type}
                          size="small"
                          color={p.proxy_type === 'MTPROTO' ? 'success' : p.proxy_type === 'SOCKS5' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {p.linked_accounts.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {p.linked_accounts.map(acc => (
                              <Chip
                                key={acc.id}
                                label={acc.name}
                                size="small"
                                color="primary"
                                variant="outlined"
                                component={Link}
                                to={`/accounts/${acc.id}`}
                                clickable
                                sx={{
                                  fontSize: '0.75rem',
                                  height: 24,
                                  textDecoration: 'none',
                                  '&:hover': {
                                    bgcolor: '#DBEAFE',
                                    textDecoration: 'underline'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography fontSize="0.8rem" color="text.secondary">
                            Не привязан
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={p.is_active ? 'Активен' : 'Выключен'}
                          size="small"
                          color={p.is_active ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                        {p.last_used ? new Date(p.last_used).toLocaleString('ru-RU') : 'Никогда'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openUpdate(p)} disabled={isLoading}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteProxy(p.id)} disabled={isLoading}>
                          <Delete fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => openLink(p.id)} disabled={isLoading}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Прокси не найдены. Создайте первый!
            </Typography>
          )}
        </Box>
      </Paper>

      {/* === МОДАЛКА СОЗДАНИЯ === */}
      <Modal open={openCreateModal} onClose={() => !isLoading && setOpenCreateModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2 }}>Создать прокси</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Тип прокси</InputLabel>
                <Select value={proxy_type} onChange={e => setProxyType(e.target.value)} label="Тип прокси">
                  {/*<MenuItem value="HTTP">HTTP</MenuItem>*/}
                  {/*<MenuItem value="SOCKS4">SOCKS4</MenuItem>*/}
                  <MenuItem value="SOCKS5">SOCKS5</MenuItem>
                  <MenuItem value="MTPROTO">MTPROTO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" icon={<Info />} sx={{ fontSize: '0.85rem' }}>
                {getProxyTypeInfo().desc}
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Хост (IP)" value={host} onChange={e => setHost(e.target.value)} fullWidth required />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField label="Порт" type="number" value={port} onChange={e => setPort(e.target.value)} fullWidth required />
            </Grid>
            {getProxyTypeInfo().auth && (
              <>
                <Grid size={{ xs: 6 }}>
                  <TextField label="Логин" value={username} onChange={e => setUsername(e.target.value)} fullWidth />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                      label="Пароль"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                  onClick={() => setShowPassword(prev => !prev)}
                                  edge="end"
                              >
                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                              </IconButton>
                            </InputAdornment>
                        )
                      }}
                  />
                </Grid>
              </>
            )}
            {getProxyTypeInfo().secret && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Secret"
                  value={secret}
                  onChange={e => setSecret(e.target.value)}
                  fullWidth
                  required={proxy_type === 'MTPROTO'}
                  helperText={proxy_type === 'MTPROTO' ? 'Обязательно для MTPROTO' : ''}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                onClick={createProxy}
                disabled={isLoading || !host || !port || !proxy_type || (proxy_type === 'MTPROTO' && !secret)}
                fullWidth
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Создать'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* === МОДАЛКА ОБНОВЛЕНИЯ === */}
      <Modal open={openUpdateModal} onClose={() => !isLoading && setOpenUpdateModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2 }}>Редактировать прокси</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Тип прокси</InputLabel>
                <Select value={proxy_type} onChange={e => setProxyType(e.target.value)} label="Тип прокси">
                  {/*<MenuItem value="HTTP">HTTP</MenuItem>*/}
                  {/*<MenuItem value="SOCKS4">SOCKS4</MenuItem>*/}
                  <MenuItem value="SOCKS5">SOCKS5</MenuItem>
                  <MenuItem value="MTPROTO">MTPROTO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" icon={<Info />} sx={{ fontSize: '0.85rem' }}>
                {getProxyTypeInfo().desc}
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Хост" value={host} onChange={e => setHost(e.target.value)} fullWidth required />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField label="Порт" type="number" value={port} onChange={e => setPort(e.target.value)} fullWidth required />
            </Grid>
            {getProxyTypeInfo().auth && (
              <>
                <Grid size={{ xs: 6 }}>
                  <TextField label="Логин" value={username} onChange={e => setUsername(e.target.value)} fullWidth />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField label="Пароль" type="password" value={password} onChange={e => setPassword(e.target.value)} fullWidth />
                </Grid>
              </>
            )}
            {getProxyTypeInfo().secret && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Secret"
                  value={secret}
                  onChange={e => setSecret(e.target.value)}
                  fullWidth
                  required={proxy_type === 'MTPROTO'}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Checkbox checked={is_active} onChange={e => setIsActive(e.target.checked)} />}
                label="Прокси активен"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                onClick={updateProxy}
                disabled={isLoading}
                fullWidth
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Сохранить'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* === МОДАЛКА ПРИВЯЗКИ === */}
      <Modal open={openLinkModal} onClose={() => !isLoading && setOpenLinkModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2 }}>Привязать прокси к аккаунту</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Выберите аккаунт</InputLabel>
                <Select
                  value={selectedAccountId || ''}
                  onChange={e => setSelectedAccountId(e.target.value)}
                  label="Выберите аккаунт"
                >
                  <MenuItem value="">—</MenuItem>
                  {accounts.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name} ({a.phone_number})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Button
                variant="contained"
                onClick={linkToAccount}
                disabled={isLoading || !selectedAccountId}
                fullWidth
                sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}
              >
                Привязать
              </Button>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Button
                variant="contained"
                onClick={unlinkFromAccount}
                disabled={isLoading || !selectedAccountId}
                fullWidth
                sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}
              >
                Отвязать
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Container>
  );
};

export default ProxyPage;